import { useEffect, useRef, useState } from "react";

// Styles
//@ts-ignore
import style from "./SchedulesModal.module.css";

// Components
//@ts-ignore
import { ButtonWithoutIcon, Icon, SchedulesWrapper } from "@viuti/recursos";

// Services

// Models
import {
  IEmployeeSchedule,
  ISchedulesArray,
} from "../../../../models/interfaces/schedules";

// Funtions
import { TimesIcon } from "../../../../models/icons";
import { clickOusideModalCloses } from "../ServicesModal/functions/clickOusideModalCloses";
import {
  buildScheduleRegistration,
  updateEmployeeSchedulesAdapter,
} from "../../../../adapters/getSchedules/updateSchedules.adapter";
import {
  getDeleteScheduleImpact,
  getScheduleImpact,
  IAffectedReservation,
} from "../../../../services/getScheduleImpact.service";
import ScheduleImpactModal from "../ScheduleImpactModal/ScheduleImpactModal";
import {
  updateNotificationMessage,
  updateNotificationStatus,
} from "../../../../redux/states/notificationSlice";
import { useAppDispatch } from "../../../../hooks/store";
import { deleteEmployeeScheduleAdapter } from "../../../../adapters/getSchedules/deleteSchedule.adapter";
import {
  buildFallbackMap,
  getLocalHoursAdapter,
  ILocalHoursMap,
} from "../../../../adapters/getSchedules/getLocalHours.adapter";
import { useMultiSedeStore } from "@/stores/useMultiSedeStore";

interface SchedulesModalProps {
  initialSchedulesState: ISchedulesArray[];
  setInitialSchedulesState: Function;
  visible: boolean;
  setVisible: Function;
  idEmployee: number;
  onSaveSuccess?: () => void;
  employeeName?: string;
}

const SchedulesModal = ({
  initialSchedulesState,
  setInitialSchedulesState,
  visible,
  setVisible,
  idEmployee,
  onSaveSuccess,
  employeeName,
}: SchedulesModalProps) => {
  const dispatch = useAppDispatch();
  const selectedLocalId = useMultiSedeStore((state) => state.selectedLocalId);
  const [employeeSchedules, setEmployeeSchedules] = useState([]);
  const [localHours, setLocalHours] = useState<ILocalHoursMap>(
    buildFallbackMap()
  );
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // US2 spec 016 — impacto del cambio de horario sobre reservas futuras.
  const [impactModalVisible, setImpactModalVisible] = useState(false);
  const [impactLoading, setImpactLoading] = useState(false);
  const [affectedReservations, setAffectedReservations] = useState<
    IAffectedReservation[]
  >([]);
  // Spec 016 (#9): borrado de una franja guardada pendiente de confirmar en el
  // modal de impacto. `resolve` responde al botón "−" (true = se borró).
  const pendingDeleteRef = useRef<{
    schedule: IEmployeeSchedule;
    resolve: (deleted: boolean) => void;
  } | null>(null);

  // Carga el horario de atencion del local para limitar las opciones del selector.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      const hours = await getLocalHoursAdapter(selectedLocalId);
      if (!cancelled) setLocalHours(hours);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, selectedLocalId]);

  // Close when clicking outside the modal
  const modalLayout = useRef(null);
  useEffect(() => {
    clickOusideModalCloses(modalLayout, setVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalLayout]);

  // Apply initial services state
  useEffect(() => {
    if (initialSchedulesState?.length > 0) {
      setEmployeeSchedules(initialSchedulesState);
    }
  }, [initialSchedulesState]);

  // Disable submit button if there are no changes
  useEffect(() => {
    if (
      JSON.stringify(initialSchedulesState) ===
      JSON.stringify(employeeSchedules)
    ) {
      setIsSubmitDisabled(true);
    } else {
      setIsSubmitDisabled(false);
    }
  }, [initialSchedulesState, employeeSchedules]);

  // Persiste el horario; si reassign=true reasigna las reservas afectadas.
  const persistSchedules = async (reassignAffected: boolean) => {
    await updateEmployeeSchedulesAdapter(
      dispatch,
      idEmployee,
      employeeSchedules,
      onSaveSuccess,
      reassignAffected
    );
  };

  const submitSchedules = async () => {
    setIsSubmitLoading(true);
    setIsSubmitDisabled(true);
    try {
      // 1) Construye el payload (puede lanzar si hay horarios incompletos).
      const scheduleRegistration = buildScheduleRegistration(employeeSchedules);

      // 2) Previsualiza el impacto del cambio (no persiste).
      const impact = await getScheduleImpact(idEmployee, scheduleRegistration);

      if (impact.isSuccess && impact.data.affectedCount > 0) {
        // 3a) Hay reservas afectadas: abrir modal de confirmacion.
        setAffectedReservations(impact.data.affected);
        setImpactModalVisible(true);
        setIsSubmitLoading(false);
        setIsSubmitDisabled(false);
        return;
      }

      // 3b) Sin afectadas (o no se pudo calcular): guardado directo.
      await persistSchedules(false);
    } catch (error: any) {
      dispatch(
        updateNotificationMessage(
          "Error al actualizar los horarios. " + (error?.message || "")
        )
      );
      dispatch(updateNotificationStatus(400));
    }
    setIsSubmitLoading(false);
    setIsSubmitDisabled(false);
  };

  const runDeleteSchedule = (
    schedule: IEmployeeSchedule,
    reassignAffected: boolean
  ): Promise<boolean> =>
    deleteEmployeeScheduleAdapter(
      schedule.idSchedule,
      dispatch,
      employeeSchedules,
      setInitialSchedulesState,
      setIsSubmitDisabled,
      setIsSubmitLoading,
      reassignAffected
    );

  // Cerrar el modal de impacto (Cancelar / X) cancela un borrado pendiente.
  const handleImpactVisible = (value: boolean) => {
    setImpactModalVisible(value);
    if (!value && pendingDeleteRef.current) {
      pendingDeleteRef.current.resolve(false);
      pendingDeleteRef.current = null;
    }
  };

  // Confirmacion del modal de impacto: persiste reasignando los afectados
  // (guardado del horario o eliminación de una franja).
  const onConfirmImpact = async () => {
    setImpactLoading(true);
    const pending = pendingDeleteRef.current;
    if (pending) {
      pendingDeleteRef.current = null;
      const deleted = await runDeleteSchedule(pending.schedule, true);
      pending.resolve(deleted);
    } else {
      await persistSchedules(true);
    }
    setImpactLoading(false);
    setImpactModalVisible(false);
  };

  // Spec 016 (#9): antes de eliminar una franja guardada se previsualiza el
  // impacto; si deja reservas fuera del horario se pide confirmación con el
  // mismo modal del guardado y, al confirmar, pasan a "Por asignar".
  const onDeleteSchedule = async (
    schedule: IEmployeeSchedule
  ): Promise<boolean> => {
    if (String(schedule.idSchedule).includes("static")) {
      return runDeleteSchedule(schedule, false);
    }
    const impact = await getDeleteScheduleImpact(Number(schedule.idSchedule));
    if (impact.isSuccess && impact.data.affectedCount > 0) {
      setAffectedReservations(impact.data.affected);
      setImpactModalVisible(true);
      return new Promise<boolean>((resolve) => {
        pendingDeleteRef.current = { schedule, resolve };
      });
    }
    return runDeleteSchedule(schedule, false);
  };

  return (
    <div
      className={`${style.layoutContainer}  ${
        visible ? style.visible : style.hidden
      }`}
      ref={modalLayout}
    >
      <div className={style.modalContainer}>
        <div className={style.headerContainer}>
          <h1 className={style.headerTitle}>Horarios</h1>
          <button
            className={style.closeModalBtn}
            onClick={() => setVisible(false)}
          >
            <Icon path={TimesIcon} alt="X" size={14} color={"#B4B4B4"} />
          </button>
        </div>
        <div className={style.schedulesContainer}>
          <SchedulesWrapper
            schedules={employeeSchedules}
            setSchedules={setEmployeeSchedules}
            scheduleActions={{ onDelete: onDeleteSchedule }}
            localHours={localHours}
          />
        </div>
        <div className={style.bottomContainer}>
          <ButtonWithoutIcon
            isPrimary
            textBttn={"Guardar"}
            handleClick={submitSchedules}
            isDisabled={isSubmitDisabled}
            isLoading={isSubmitLoading}
          />
        </div>
      </div>

      <ScheduleImpactModal
        visible={impactModalVisible}
        setVisible={handleImpactVisible}
        affected={affectedReservations}
        employeeName={employeeName}
        onConfirm={onConfirmImpact}
        isLoading={impactLoading}
      />
    </div>
  );
};

export default SchedulesModal;
